import styled from '@emotion/styled';
import { gettext } from '@eventbrite/i18n';
import {
    getFormValidationAdornment,
    Switch,
    TextField,
    Typography,
} from '@eventbrite/marmalade';
import React, { ChangeEvent } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AutoCreateEventFormValues } from '../types';

const ZERO = 0;

export enum TicketsPriceFormFieldTestId {
    TicketsPrice = 'AutoCreateEventTicketsPriceFormTicketsPrice',
    FreeSwitch = 'AutoCreateEventTicketsPriceFormFreeSwitch',
}

interface TicketsPriceFormFieldProps {
    currencySymbol: string;
}

export const TicketsPriceFormField: React.FC<TicketsPriceFormFieldProps> = ({
    currencySymbol,
}) => {
    const { setValue, control, watch } =
        useFormContext<AutoCreateEventFormValues>();

    const areTicketsFree = watch('areTicketsFree');

    const formatPrice = (event: ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target;

        // To force the error on empty field or format the value to 2 decimals
        setValue('ticketsPrice', (Number(value) || ZERO).toFixed(2), {
            shouldValidate: true,
        });
    };

    return (
        <>
            <Title>
                <Typography as="h2" color="neutral-900" variant="body-lg">
                    {gettext('How much do you want to charge for tickets?')}
                </Typography>
            </Title>
            <Subtitle>
                <Typography as="p" color="neutral-1000" variant="body-md">
                    {gettext(
                        'Our tool can only generate one General Admission ticket for now. You can edit and add more ticket types later.',
                    )}
                </Typography>
            </Subtitle>
            <InputArea>
                <Controller
                    name="ticketsPrice"
                    control={control}
                    rules={{
                        required: {
                            value: !areTicketsFree,
                            message: gettext(
                                'Ticket price is required',
                            ).toString(),
                        },
                    }}
                    render={({ field, fieldState: { error } }) => (
                        <TextField
                            aria-label={gettext('Set tickets price').toString()}
                            data-testid={
                                TicketsPriceFormFieldTestId.TicketsPrice
                            }
                            disabled={areTicketsFree}
                            hasError={!!error}
                            helperText={error && error.message}
                            id="auto-create-form-ticket-price"
                            inputMode="decimal"
                            label={gettext('Price').toString()}
                            placeholder={ZERO.toFixed(2)}
                            {...field}
                            value={field.value || ''}
                            onChange={(value) => {
                                field.onChange({
                                    target: {
                                        value,
                                    },
                                });
                            }}
                            onBlur={formatPrice}
                            step={0.01}
                            type="number"
                            validationAdornment={getFormValidationAdornment({
                                hasError: !!error,
                            })}
                            required
                            prefix={currencySymbol}
                        />
                    )}
                />
            </InputArea>
            <Label>
                <Controller
                    name="areTicketsFree"
                    control={control}
                    render={({ field }) => {
                        return (
                            <Switch
                                aria-label={gettext(
                                    'My tickets are free',
                                ).toString()}
                                name={field.name}
                                checked={areTicketsFree}
                                onCheckedChange={(areTicketsFree) => {
                                    field.onChange({
                                        target: { value: areTicketsFree },
                                    });
                                    setValue('ticketsPrice', ZERO.toFixed(2), {
                                        shouldValidate: areTicketsFree,
                                    });
                                }}
                                data-testid={
                                    TicketsPriceFormFieldTestId.FreeSwitch
                                }
                            />
                        );
                    }}
                />
                <span>{gettext('My tickets are free')}</span>
            </Label>
        </>
    );
};

const Title = styled.div`
    margin-bottom: 8px;
`;

const Subtitle = styled.section`
    margin-bottom: 24px;
`;

const InputArea = styled.div`
    margin-bottom: 12px;
    max-width: 180px;
`;

const Label = styled.label`
    align-items: center;
    cursor: pointer;
    display: inline-flex;
    flex-direction: row;
    font-size: 14px;
    gap: 8px;
    justify-content: flex-start;
    margin-bottom: 16px;
`;
